'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Chat } from './Chat';

// ---------------------------------------------------------------------------
// Floating support launcher + chat panel
// ---------------------------------------------------------------------------

const NUDGE_KEY = 'floradine.chat.nudgeDismissed';

export const ChatBot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Surface a one-time welcome nudge a few seconds after load, unless the
  // visitor already dismissed it or opened the chat this session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(NUDGE_KEY)) return;

    // const t = setTimeout(() => setShowNudge(true), 4000);
    // return () => clearTimeout(t);
  }, []);

  // Close on Escape and return focus to the launcher.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dismissNudge = () => {
    setShowNudge(false);
    try {
      sessionStorage.setItem(NUDGE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const openChat = () => {
    setOpen(true);
    dismissNudge();
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {/* Chat panel */}
      {open && <Chat onClose={() => setOpen(false)} />}

      <div className="flex items-end gap-2.5">
        {/* Welcome nudge */}
        {!open && showNudge && (
          <div className="relative mb-1 max-w-60 animate-slide-up rounded-2xl rounded-br-sm border border-border bg-surface px-3.5 py-2.5 shadow-organic">
            <button
              type="button"
              onClick={dismissNudge}
              aria-label="Dismiss"
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface text-muted-foreground shadow-subtle transition-colors hover:bg-muted"
            >
              <Icon name="XMarkIcon" size={12} />
            </button>
            <p className="text-sm leading-snug text-foreground">
              Questions about our eco-tableware? Ask our team anything 🌱
            </p>
          </div>
        )}

        {/* Launcher button */}
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls="livechat-popup"
          aria-label={open ? 'Close support chat' : 'Open support chat'}
          onClick={() => (open ? setOpen(false) : openChat())}
          className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-dramatic transition duration-200 hover:scale-105 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary active:scale-95"
        >
          {/* Idle attention pulse (paused once open) */}
          {!open && (
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/40 [animation-duration:2.5s]" />
          )}
          <span className="relative">
            <Icon
              name={open ? 'XMarkIcon' : 'ChatBubbleLeftRightIcon'}
              size={26}
              className="text-primary-foreground"
            />
          </span>
          {/* Online indicator */}
          {!open && (
            <span className="absolute right-0.5 top-0.5 h-3 w-3 rounded-full border-2 border-primary bg-success" />
          )}
        </button>
      </div>
    </div>
  );
};
